/**
 * InstallmentScheduleEditor — editor per-rata del piano di recupero:
 * per ogni rata una busta paga (mese/anno, anche XIII/XIV) + importo.
 * Regola: massimo una rata per busta paga (per lo stesso acconto).
 */
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { BustaPagaSelector, parseBustaPaga, formatBustaPaga } from "./BustaPagaSelector";
import { fmtEur } from "../lib/format";

export const round2 = (x) => Math.round(x * 100) / 100;

export const nextBpLabel = (label) => {
    const { month, year } = parseBustaPaga(label);
    if (!month || !year) return "";
    if (month === "XIII") return formatBustaPaga("01", String(parseInt(year, 10) + 1));
    if (month === "XIV") return formatBustaPaga("08", year);
    let m = parseInt(month, 10) + 1;
    let y = parseInt(year, 10);
    if (m > 12) { m = 1; y += 1; }
    return formatBustaPaga(String(m).padStart(2, "0"), String(y));
};

export const buildConsecutiveLabels = (baseBp, n) => {
    const out = [];
    let cur = baseBp || "";
    for (let i = 0; i < n; i++) {
        out.push(cur);
        cur = nextBpLabel(cur);
    }
    return out;
};

export const evenSplit = (residual, m) => {
    if (m <= 0) return [];
    const base = round2(residual / m);
    const arr = Array(m).fill(base);
    arr[m - 1] = round2(residual - base * (m - 1));
    return arr;
};

export const validateScheduleRows = (rows, totalAmount) => {
    if (rows.some((r) => !r.bp)) return "Seleziona la busta paga per ogni rata";
    const labels = rows.map((r) => r.bp);
    if (new Set(labels).size !== labels.length) return "Ogni rata deve essere su una busta paga diversa";
    const amounts = rows.map((r) => parseFloat(r.amount));
    if (amounts.some((a) => !Number.isFinite(a) || a <= 0)) return "Tutte le rate devono avere un importo positivo";
    const sum = round2(amounts.reduce((a, b) => a + b, 0));
    if (Math.abs(sum - (parseFloat(totalAmount) || 0)) > 0.01) return "La somma delle rate deve coincidere con l'importo totale";
    return null;
};

export const InstallmentScheduleEditor = ({ rows, onChange, lockedCount = 0, totalAmount = 0, testidPrefix = "sched" }) => {
    const amounts = rows.map((r) => parseFloat(r.amount));
    const sum = round2(amounts.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0));
    const total = parseFloat(totalAmount) || 0;
    const diff = round2(total - sum);
    const filled = rows.map((r) => r.bp).filter(Boolean);
    const hasDupes = new Set(filled).size !== filled.length;
    const unlocked = rows.length - lockedCount;

    const setRow = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

    const distribute = () => {
        const lockedSum = round2(rows.slice(0, lockedCount).reduce((a, r) => a + (parseFloat(r.amount) || 0), 0));
        const split = evenSplit(round2(total - lockedSum), unlocked);
        onChange(rows.map((r, i) => (i < lockedCount ? r : { ...r, amount: String(split[i - lockedCount]) })));
    };

    return (
        <div className="space-y-1.5" data-testid={`${testidPrefix}-editor`}>
            {rows.map((r, i) => (
                <div
                    key={i}
                    className={`flex flex-wrap items-center gap-2 rounded-md border px-2.5 py-1.5 ${i < lockedCount ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}
                    data-testid={`${testidPrefix}-row-${i}`}
                >
                    <span className="w-12 shrink-0 text-[11px] font-semibold text-slate-600">Rata {i + 1}</span>
                    <div className="min-w-[220px] flex-1">
                        <BustaPagaSelector
                            compact
                            value={r.bp}
                            onChange={(v) => setRow(i, { bp: v })}
                            disabled={i < lockedCount}
                            testidPrefix={`${testidPrefix}-row-${i}`}
                        />
                    </div>
                    <div className="relative w-28">
                        <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={r.amount}
                            onChange={(e) => setRow(i, { amount: e.target.value })}
                            className="h-8 pr-6 text-xs"
                            data-testid={`${testidPrefix}-row-${i}-amount`}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">€</span>
                    </div>
                    {i < lockedCount && (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600" data-testid={`${testidPrefix}-row-${i}-locked`}>
                            trattenuta
                        </span>
                    )}
                </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <div className="text-[11px]">
                    {Math.abs(diff) <= 0.005 ? (
                        <span className="font-medium text-emerald-700" data-testid={`${testidPrefix}-sum-ok`}>
                            Totale rate: {fmtEur(sum)} ✓
                        </span>
                    ) : (
                        <span className="font-medium text-red-600" data-testid={`${testidPrefix}-sum-warning`}>
                            Totale rate {fmtEur(sum)} — {diff > 0 ? `mancano ${fmtEur(diff)}` : `eccedenza di ${fmtEur(-diff)}`}
                        </span>
                    )}
                    {hasDupes && (
                        <span className="ml-3 font-medium text-red-600" data-testid={`${testidPrefix}-dupe-warning`}>
                            Due rate sulla stessa busta paga
                        </span>
                    )}
                </div>
                {unlocked > 0 && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px]"
                        onClick={distribute}
                        data-testid={`${testidPrefix}-distribute-btn`}
                    >
                        Dividi il residuo {lockedCount > 0 ? "sulle rate future" : "equamente"}
                    </Button>
                )}
            </div>
        </div>
    );
};
